/**
 * Схеми валідації запитів. Сервер перевіряє ними вхід, фронтенд бере з них типи —
 * контракт API описаний один раз.
 */
import { z } from 'zod';
import { DEFAULT_LEAVE_SCHEDULE, DEFAULT_POSITION_CODE, emptyThemePalette } from './defaults.js';
import { MARK_LAYERS, TEMPLATE_KINDS, USER_ROLES } from './types.js';
export const zId = z.string().min(1).max(64);
/**
 * Звільнення групи в один тип дня. Час — «19:10» або порожньо: порожнє поле
 * означає «за загальним розпорядком», і саме воно стоїть у більшості груп.
 */
const zTimeOrEmpty = z
    .string()
    .regex(/^$|^([01]\d|2[0-3]):[0-5]\d$/, 'Час у вигляді 19:10')
    .default('');
const zLeaveHours = z
    .object({ from: zTimeOrEmpty, to: zTimeOrEmpty })
    .default({ from: '', to: '' });
const zLeaveDay = z
    .object({
    on: z.boolean().default(true),
    from: zTimeOrEmpty,
    to: zTimeOrEmpty,
    /** Години окремих шаблонів харчування: кияни й решта ходять не разом. */
    byPattern: z.record(z.string().max(64), zLeaveHours).default({}),
})
    .default({ on: true, from: '', to: '', byPattern: {} });
const zTemplateHours = z
    .object({
    dayFrom: zTimeOrEmpty,
    dayTo: zTimeOrEmpty,
    from: zTimeOrEmpty,
    to: zTimeOrEmpty,
    goodFrom: zTimeOrEmpty,
    goodTo: zTimeOrEmpty,
})
    .default({ dayFrom: '', dayTo: '', from: '', to: '', goodFrom: '', goodTo: '' });
/** Розпорядок одного типу дня: кожне поле має типове значення цього дня. */
const zScheduleDay = (d) => z
    .object({
    dayFrom: zTimeOrEmpty.default(d.dayFrom),
    dayTo: zTimeOrEmpty.default(d.dayTo),
    from: zTimeOrEmpty.default(d.from),
    toRegular: zTimeOrEmpty.default(d.toRegular),
    toMorning: zTimeOrEmpty.default(d.toMorning),
    reportFrom: zTimeOrEmpty.default(d.reportFrom),
    reportOvernight: z.boolean().default(d.reportOvernight),
    handoverFrom: zTimeOrEmpty.default(d.handoverFrom),
    beforeDutyFrom: zTimeOrEmpty.default(d.beforeDutyFrom),
    beforeDutyTo: zTimeOrEmpty.default(d.beforeDutyTo),
    goodFrom: zTimeOrEmpty.default(d.goodFrom),
    goodTo: zTimeOrEmpty.default(d.goodTo),
    kyivGoodFrom: zTimeOrEmpty.default(d.kyivGoodFrom),
    kyivGoodTo: zTimeOrEmpty.default(d.kyivGoodTo),
    kyivDayFrom: zTimeOrEmpty.default(d.kyivDayFrom),
    kyivDayTo: zTimeOrEmpty.default(d.kyivDayTo),
    kyivFrom: zTimeOrEmpty.default(d.kyivFrom),
    byPattern: z.record(z.string().max(64), zTemplateHours).optional(),
})
    .default(d);
const leaveScheduleSchema = z
    .object({
    weekday: zScheduleDay(DEFAULT_LEAVE_SCHEDULE.weekday),
    saturday: zScheduleDay(DEFAULT_LEAVE_SCHEDULE.saturday),
    sunday: zScheduleDay(DEFAULT_LEAVE_SCHEDULE.sunday),
})
    .default(DEFAULT_LEAVE_SCHEDULE);
export const zIsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата має бути у форматі РРРР-ММ-ДД');
export const zSlug = z.string().regex(/^[a-z0-9][a-z0-9-]{1,30}$/, 'Лише латиниця, цифри та дефіс');
/* ------------------------------------------------------------------- вхід */
export const loginSchema = z.object({
    login: z.string().min(1, 'Вкажіть логін').max(64),
    password: z.string().min(1, 'Вкажіть пароль').max(200),
    org: zSlug.optional(),
    remember: z.boolean().optional(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8, 'Мінімум 8 символів').max(200),
});
/* ---------------------------------------------------------------- позначки */
export const markOpSchema = z.object({
    personId: zId,
    date: zIsoDate,
    meal: z.number().int().min(0).max(15),
    kind: z.string().min(1).max(16).nullable(),
    layer: z.enum(MARK_LAYERS).optional(),
});
export const markBatchSchema = z.object({
    ops: z.array(markOpSchema).min(1).max(20000),
    /** Ідентифікатор вкладки — щоб не повертати їй же власні зміни через SSE. */
    clientId: z.string().max(64).optional(),
});
export const marksQuerySchema = z.object({
    from: zIsoDate,
    to: zIsoDate,
    unitId: zId.optional(),
});
/** Скільки останніх правок таблиці показати — з меню «Вигляд». */
export const recentMarksQuerySchema = z.object({
    from: zIsoDate.optional(),
    to: zIsoDate.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(20),
    /** Шари через кому: таблиця нарядів дивиться свій, харчування — свій. */
    layers: z
        .string()
        .optional()
        .transform(v => (v ? v.split(',').filter(l => MARK_LAYERS.includes(l)) : ['main'])),
    /** Курсор: правки, старші за цю. Так історію гортають углиб пачками. */
    before: z.coerce.number().int().positive().optional(),
});
/* -------------------------------------------------------------------- люди */
/** Клітинка тижня: у легасі й SQLite інколи 0/1 замість true/false. */
const weekCellSchema = z
    .union([z.boolean(), z.literal(0), z.literal(1)])
    .transform(v => v === true || v === 1);
export const weekTemplateSchema = z.array(z.array(weekCellSchema).max(15)).length(7).nullable();
export const personSchema = z.object({
    unitId: zId.nullable().optional(),
    ord: z.number().int().optional(),
    rankCode: z.string().max(32).default(''),
    lastName: z.string().min(1, 'Вкажіть прізвище').max(80),
    firstName: z.string().max(80).default(''),
    patronymic: z.string().max(80).default(''),
    taxCode: z.string().max(20).default(''),
    /** Зріст понад 190 см — окрема норма в розході. */
    tall: z.boolean().default(false),
    /** Хорошист: щодня виходить у звільнення з окремої години розпорядку. */
    goodStudent: z.boolean().default(false),
    sex: z.enum(['m', 'f']).optional(),
    positionCode: z.string().max(32).nullable().optional().default(DEFAULT_POSITION_CODE),
    /** Відділення в межах групи; 0 — не розподілений. */
    squad: z.number().int().min(0).max(20).default(0),
    patternId: z.string().max(32).nullable().optional(),
    template: weekTemplateSchema.optional(),
    declensions: z
        .object({ dat: z.string().max(200), nom: z.string().max(200), gen: z.string().max(200) })
        .partial()
        .optional(),
    telegramId: z.number().int().nullable().optional(),
    active: z.boolean().default(true),
    note: z.string().max(500).default(''),
    /** Куди їде у звільнення, за яким телефоном шукати і де ховається. */
    address: z.string().max(300).default(''),
    phone: z.string().max(60).default(''),
    shelter: z.string().max(300).default(''),
    /** Часті підстави звільнення: кнопки в боті на кроці «у зв’язку з …». */
    leaveReasons: z.array(z.string().max(80)).max(8).default([]),
    /** Додаткові адреси звільнення: кнопки в боті на кроці «за якою адресою». */
    leaveAddresses: z.array(z.string().max(300)).max(8).default([]),
});
export const personPatchSchema = personSchema.partial();
/** Поділ груп навпіл по відділеннях. Порожньо — усі групи курсу. */
export const splitSquadsSchema = z.object({
    unitId: zId.nullable().optional(),
});
/* ------------------------------------------------------------- довідники */
export const unitSchema = z.object({
    name: z.string().min(1).max(80),
    kind: z.string().max(60).default(''),
    parentId: zId.nullable().optional(),
    ord: z.number().int().optional(),
});
export const rankSchema = z.object({
    code: z.string().min(1).max(32),
    name: z.string().min(1).max(80),
    gen: z.string().max(80).default(''),
    dat: z.string().max(80).default(''),
    short: z.string().max(40).default(''),
    ord: z.number().int().optional(),
});
export const positionSchema = z.object({
    code: z.string().min(1).max(32),
    name: z.string().min(1).max(120),
    nameDat: z.string().max(120).default(''),
    short: z.string().max(16).default(''),
    ord: z.number().int().optional(),
});
export const officialRoleSchema = z.object({
    kind: z.string().min(1).max(32),
    position: z.string().max(160).default(''),
    positionDat: z.string().max(160).default(''),
    note: z.string().max(160).default(''),
});
export const officialSchema = z.object({
    lastName: z.string().min(1).max(80),
    firstName: z.string().max(80).default(''),
    patronymic: z.string().max(80).default(''),
    gen: z.string().max(160).default(''),
    rank: z.string().max(60).default(''),
    roles: z.array(officialRoleSchema).max(8).default([]),
    active: z.boolean().default(true),
    ord: z.number().int().optional(),
});
/* --------------------------------------------------------- налаштування */
export const mealSchema = z.object({
    code: z.string().min(1).max(16),
    name: z.string().min(1).max(40),
    short: z.string().min(1).max(4),
    accusative: z.string().max(40).default(''),
});
export const markTypeSchema = z.object({
    code: z.string().min(1).max(16),
    letter: z.string().min(1).max(2),
    label: z.string().max(120).default(''),
    color: z.string().max(32),
    textColor: z.string().max(32).default('#ffffff'),
    colorDark: z.string().max(32).optional(),
    textColorDark: z.string().max(32).optional(),
    counted: z.boolean(),
    // 93 = 31 доба на три прийоми їжі: довшої за місяць позначки не буває,
    // а коротка стеля не давала завести наряд навіть на тиждень.
    spread: z.number().int().min(1).max(93).default(1),
    dutySpread: z.number().int().min(1).max(93).optional(),
    eatsInCanteen: z.boolean().default(false),
    duty: z.boolean().optional(),
    dutyOnly: z.boolean().optional(),
    transferToMeals: z.boolean().optional(),
    startMeal: z.number().int().min(0).max(14).optional(),
    dutyCountsAs: z
        .object({ code: z.string().max(16), weight: z.number().min(0).max(10) })
        .optional(),
    keepsLeave: z.boolean().optional(),
    changesLeave: z.boolean().optional(),
    /** «хвороба», «відпустка» — чому цього дня людини на службі немає. */
    awayReason: z.string().max(40).optional(),
    /** Позначка таблиці «Статус о/с». */
    away: z.boolean().optional(),
    /** Чи писати позначку в дужках розходу ВМЗ. */
    inExpense: z.boolean().optional(),
});
export const mealPatternSchema = z.object({
    id: z.string().min(1).max(32),
    name: z.string().min(1).max(60),
    hint: z.string().max(160).default(''),
    days: z.array(z.array(weekCellSchema).max(15)).length(7),
    isDefault: z.boolean().default(false),
});
const hexColor = z
    .string()
    .regex(/^(#[0-9a-fA-F]{6})?$/, 'Колір у форматі #RRGGBB')
    .default('');
const themePaletteSchema = z
    .object({
    bg: hexColor,
    panel: hexColor,
    panel2: hexColor,
    line: hexColor,
    text: hexColor,
    muted: hexColor,
})
    .default(emptyThemePalette());
export const orgSettingsSchema = z.object({
    /** Колір акценту: шість шістнадцяткових цифр, `rainbow` (переливається) або порожньо — типовий. */
    accent: z
        .string()
        .regex(/^(#[0-9a-fA-F]{6}|rainbow)?$/, 'Колір у форматі #RRGGBB')
        .default(''),
    palettes: z
        .object({
        light: themePaletteSchema,
        dark: themePaletteSchema,
    })
        .default({ light: emptyThemePalette(), dark: emptyThemePalette() }),
    locale: z.enum(['uk', 'ru', 'en']),
    meals: z.array(mealSchema).min(1).max(10),
    mealPatterns: z.array(mealPatternSchema).max(20).default([]),
    markTypes: z.array(markTypeSchema).min(1).max(14),
    weekStart: z.union([z.literal(0), z.literal(1)]),
    documents: z.object({
        institution: z.string().max(300),
        institutionDat: z.string().max(300),
        /** Курс для {COURSE}. Раніше лежав у `report.course` — звідти його переносить fixup. */
        course: z.string().max(120).default(''),
        /** Курс коротко для таблиць: «С2 МК». */
        courseShort: z.string().max(40).default(''),
        city: z.string().max(80),
        clerkName: z.string().max(120),
        clerkPhone: z.string().max(60),
        defaultTemplates: z.record(z.string().max(32), z.string().max(64)).default({}),
        kindOfficials: z
            .record(z.string().max(32), z.record(z.string().max(32), z.string().max(64)))
            .default({}),
        /** Вигляд «Розходу ВМЗ»; старі налаштування знають лише перший. */
        expenseTally: z.enum(['eating', 'away']).default('eating'),
        /** Час і підстава рапорту на добове; старі налаштування їх не мають. */
        dutyReport: z
            .object({
            from: z.string().max(5).default('15:15'),
            to: z.string().max(5).default('07:30'),
            reason: z.string().max(300).default(''),
        })
            .default({ from: '15:15', to: '07:30', reason: '' }),
        /** Сума винагороди й чинні стягнення; старі налаштування їх не мають. */
        bonusReport: z
            .object({
            amount: z.string().max(20).default(''),
            penalized: z.array(z.string().max(64)).max(500).default([]),
        })
            .default({ amount: '', penalized: [] }),
    }),
    tableSignatures: z
        .array(z.object({
        officialId: zId,
        roleKind: z.string().max(32),
    }))
        .max(10)
        .default([]),
    /** Два місця підпису списку звільнення: «ЗАТВЕРДЖУЮ» і той, хто склав. */
    leaveListSigners: z
        .object({
        approve: z
            .object({ officialId: z.string().max(64).default(''), roleKind: z.string().max(32).default('') })
            .default({ officialId: '', roleKind: '' }),
        /** Старшина курсу — людина зі складу. */
        authorId: z.string().max(64).default(''),
    })
        .default({ approve: { officialId: '', roleKind: '' }, authorId: '' }),
    /** Розпорядок звільнення груп: порожньо — усі ходять за спільним. */
    leaveUnits: z
        .array(z.object({
        unitId: zId,
        weekday: zLeaveDay,
        saturday: zLeaveDay,
        sunday: zLeaveDay,
    }))
        .max(200)
        .default([]),
    /** Години звільнення для позначки за типами дня. */
    leaveMarks: z
        .record(z.string().max(32), z
        .object({
        weekday: zTimeOrEmpty,
        saturday: zTimeOrEmpty,
        sunday: zTimeOrEmpty,
        noLeave: z.boolean().optional(),
    })
        .default({ weekday: '', saturday: '', sunday: '' }))
        .default({}),
    /** Загальний розпорядок звільнення; кожне поле має типове значення. */
    leaveSchedule: leaveScheduleSchema,
    tableTemplate: z.object({
        firstRow: z.number().int().min(1).max(2000),
        lastRow: z.number().int().min(1).max(5000),
        dayHeaderRow: z.number().int().min(1).max(2000),
        firstDayCol: z.number().int().min(1).max(500),
        columns: z.object({
            num: z.number().int().min(0).max(500),
            rank: z.number().int().min(0).max(500),
            name: z.number().int().min(1).max(500),
            unit: z.number().int().min(0).max(500),
            code: z.number().int().min(0).max(500),
        }),
        totalColumn: z.string().max(4),
        /** Рядки шапки бланка; старі налаштування їх не мають — доливає fixup. */
        title: z
            .array(z.object({ cell: z.string().max(8), text: z.string().max(400) }))
            .max(6)
            .default([]),
        signatureCells: z
            .array(z.object({
            position: z.string().max(8).optional(),
            rank: z.string().max(8).optional(),
            name: z.string().max(8).optional(),
            date: z.string().max(8).optional(),
            dateMask: z.string().max(20).optional(),
        }))
            .max(12),
    }),
    canteen: z.object({
        title: z.array(z.string().max(200)).max(6),
        /** Хто підписує список; старі налаштування цього поля не мають. */
        signOfficial: z.string().max(64).default(''),
    }),
    report: z.object({
        body: z.string().max(4000),
        /** Посадовці для «рапортів в один клік»: там їх не вибирають щоразу. */
        quickOfficials: z.record(z.string().max(32), z.string().max(64)).default({}),
    }),
    duties: z.object({
        markKind: z.string().min(1).max(16),
        sectionMarks: z
            .array(z.object({
            section: z.string().max(16),
            markKind: z.string().max(16).nullable(),
        }))
            .max(20),
        mealsCovered: z.number().int().min(1).max(15),
    }),
    features: z.object({
        reports: z.boolean(),
        canteenList: z.boolean(),
        expenseNote: z.boolean(),
        telegramBot: z.boolean(),
        duties: z.boolean(),
    }),
    bot: z.object({
        enabled: z.boolean(),
        webUrl: z.string().max(300),
        staffTelegramIds: z.array(z.number().int()).max(100),
        notifyChanges: z.boolean(),
        leaveReport: z.boolean().default(false),
        dutyReport: z.boolean().default(false),
    }),
});
export const orgSchema = z.object({
    shortName: z.string().min(1).max(60),
    slug: zSlug.optional(),
});
/** Створення нового курсу. */
export const orgCreateSchema = z.object({
    shortName: z.string().min(1).max(60),
    /** Технічний ключ курсу. Не вказано — сервер згенерує сам. */
    slug: zSlug.optional(),
    /** Узяти налаштування, довідники, посадовців і бланки з наявного курсу. */
    copyFrom: zId.optional(),
    /** Створити адміністратора курсу разом із ним. */
    adminLogin: z.string().min(3).max(64).regex(/^[\w.@-]+$/).optional(),
    adminFullName: z.string().max(120).optional(),
    adminPassword: z.string().min(8).max(200).optional(),
});
export const switchOrgSchema = z.object({ orgId: zId });
/* --------------------------------------------------------- користувачі */
export const userSchema = z.object({
    login: z.string().min(3, 'Мінімум 3 символи').max(64).regex(/^[\w.@-]+$/, 'Без пробілів'),
    fullName: z.string().max(120).default(''),
    role: z.enum(USER_ROLES),
    personId: zId.nullable().optional(),
    /** Порожньо — увесь курс; інакше лише ці групи. */
    unitIds: z.array(zId).max(50).default([]),
    /** Лише жінки — окремо від груп або разом із ними. */
    onlyFemale: z.boolean().default(false),
    /** Дозволити діловоду правити налаштування курсу. */
    settingsAccess: z.boolean().default(false),
    telegramId: z.number().int().nullable().optional(),
    active: z.boolean().default(true),
    password: z.string().min(8, 'Мінімум 8 символів').max(200).optional(),
});
export const userPatchSchema = userSchema.partial();
/* ------------------------------------------------------------- документи */
export const docRuleSchema = z.object({
    op: z.enum([
        'setParagraph',
        'setSegments',
        'replaceBetween',
        'noteAfter',
        'replaceSequence',
        'fitHeader',
        'dropEmptyAfter',
        'ensureEmptyBefore',
        'fixYears',
    ]),
    match: z.string().max(300).optional(),
    matchAll: z.array(z.string().max(300)).max(8).optional(),
    matchNone: z.array(z.string().max(300)).max(8).optional(),
    matchRegex: z.string().max(300).optional(),
    exact: z.boolean().optional(),
    value: z.string().max(6000).optional(),
    segments: z.record(z.string().max(4), z.string().max(600)).optional(),
    after: z.string().max(200).optional(),
    before: z.string().max(200).optional(),
    indent: z.number().int().min(0).max(20000).optional(),
    skipIfEmpty: z.boolean().optional(),
    when: z
        .object({ key: z.string().max(40), equals: z.string().max(300).optional(), notEquals: z.string().max(300).optional() })
        .optional(),
    note: z.string().max(200).optional(),
});
export const templateMetaSchema = z.object({
    kind: z.enum(TEMPLATE_KINDS),
    name: z.string().min(1).max(120),
    defaults: z.record(z.string().max(40), z.string().max(120)).default({}),
    rules: z.array(docRuleSchema).max(80).default([]),
});
/** Розмітка бланка: який шматок якого абзацу стає підстановкою. */
export const markSpotSchema = z.object({
    paragraph: z.number().int().min(0).max(5000),
    text: z.string().min(1).max(300),
    /**
     * Чим замінити виділене: ключ `{NAME_NOM}` або довільний текст.
     *
     * Спершу тут приймався лише ключ, і виправити в бланку описку доводилося у
     * Word — завантажити файл, поправити, залити назад. Механізм заміни й так
     * загальний, різницю робила сама перевірка.
     */
    replacement: z.string().max(300),
});
/** Список людей, вставлений повідомленням: сам текст і що з ним робити. */
export const peopleImportSchema = z.object({
    text: z.string().min(1).max(20000),
    mode: z.enum(['list', 'dative', 'tall']),
    /** Куди зарахувати новоприбулих. Для відмінків не потрібен. */
    unitId: z.string().nullable().optional(),
});
export const reportRequestSchema = z.object({
    personId: zId,
    from: zIsoDate,
    to: zIsoDate,
    templateId: zId.optional(),
    officials: z.record(z.string().max(32), zId).default({}),
    /** Повернути текст замість файлу — для попереднього перегляду. */
    preview: z.boolean().optional(),
    /**
     * Незбережені налаштування: сторінка «Рапорт» показує, як виглядатиме
     * документ із поточною чернеткою, ще до натискання «Зберегти».
     * Береться лише разом із preview і нікуди не записується.
     */
    draft: orgSettingsSchema.optional(),
    /** Повернути PDF замість .docx — точний перегляд «як у Word». */
    pdf: z.boolean().optional(),
});
/**
 * Рапорт на виплату винагороди за місяць: період і ті, кому не виплачується.
 * Кого вписати в перший список, програма бачить із таблиці — обирати людей
 * поіменно не треба.
 */
export const bonusRequestSchema = z.object({
    from: zIsoDate,
    to: zIsoDate,
    templateId: zId.optional(),
    officials: z.record(z.string().max(32), zId).default({}),
    /** У кого чинне стягнення; без поля береться список із налаштувань. */
    penalized: z.array(zId).max(500).optional(),
    draft: orgSettingsSchema.optional(),
});
export const expenseRequestSchema = z.object({
    from: zIsoDate,
    to: zIsoDate,
    showZero: z.boolean().default(true),
});
export const canteenRequestSchema = z.object({ date: zIsoDate });
/**
 * Список звільнення на дату. `mode` — примусовий режим дня: свята та
 * перенесені дні календар не знає, тож діловод має право сказати сам.
 */
export const leaveListRequestSchema = z.object({
    date: zIsoDate,
    mode: z.enum(['weekday', 'saturday', 'sunday']).nullish(),
    /**
     * Аркуш «дві копії поруч» замість двох сторінок. Саме його друкують, тому
     * він і типовий: розріз тоді проходить рівно посередині аркуша.
     *
     * Прапорець приходить і тілом запиту, і рядком у посиланні, тому «false»
     * розбираємо самі: `z.coerce.boolean()` тут не годиться — для нього будь-який
     * непорожній рядок, зокрема й «false», означає «так».
     */
    twoUp: z
        .union([z.boolean(), z.enum(['true', 'false', '1', '0']).transform(v => v === 'true' || v === '1')])
        .default(true),
});
/**
 * Рапорт на звільнення — підстава на конкретну дату, тому й запит на дату.
 *
 * Час у рапорті в кожного свій, і придумувати його програма не має права:
 * порожній рядок означає «за розпорядком дня», а не «з нуля».
 */
export const zLeaveSpan = z
    .string()
    .trim()
    .max(16)
    .refine(v => v === '' || /^\d{1,2}[:.]\d{2}\s*[-\u2013\u2014]\s*\d{1,2}[:.]\d{2}$/.test(v), {
    message: 'Час пишеться як «15:15-07:30»',
});
export const leaveReportsQuerySchema = z.object({ date: zIsoDate });
/** До якого моменту діловод бачив рапорти з бота: час SQLite за UTC. */
export const leaveNewsSeenSchema = z.object({
    until: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, 'Час має бути у форматі РРРР-ММ-ДД ГГ:ХХ:СС'),
});
/** Рапорти за проміжок — для таблиці рапортів, місяць за раз. */
export const leaveReportsRangeSchema = z
    .object({ from: zIsoDate, to: zIsoDate })
    .refine(r => r.from <= r.to, { message: 'Початок пізніше за кінець' });
export const leaveReportSetSchema = z.object({
    date: zIsoDate,
    personId: zId,
    /** Знято — людина йде за загальним розпорядком. */
    on: z.boolean().default(true),
    span: zLeaveSpan.default(''),
    /**
     * Звичайний рапорт чи добовий. Не вказано — вид лишається, який був
     * (новий рапорт — звичайний): правка часу добовий не перетворює.
     */
    kind: z.enum(['leave', 'duty']).optional(),
});
/** Кому звільнення цього дня закрито; причина йде в примітку списку. */
export const leaveBlockSetSchema = z.object({
    date: zIsoDate,
    personId: zId,
    /** Знято — людина повертається до списку. */
    on: z.boolean().default(true),
    reason: z.string().max(120).default(''),
});
/**
 * Перенесення звільнення групи на конкретний день.
 *
 * `override: false` знімає перенесення — група повертається до постійного
 * розкладу з налаштувань курсу.
 */
export const leaveUnitDaySetSchema = z.object({
    date: zIsoDate,
    unitId: zId,
    override: z.boolean().default(true),
    on: z.boolean().default(true),
    from: zTimeOrEmpty,
    to: zTimeOrEmpty,
});
/**
 * Перегляд документа картинкою: той самий файл, який отримає діловод, лише
 * намальований. `draft` дає побачити ще не збережені налаштування.
 */
export const docPreviewSchema = z.object({
    kind: z.string().min(1).max(32),
    /** На кому показати документ; без нього — перший зі списку. */
    personId: zId.optional(),
    date: zIsoDate.optional(),
    from: zIsoDate.optional(),
    to: zIsoDate.optional(),
    /** Час і реквізити звільнення чи добового — щоб перегляд збігався з файлом. */
    timeFrom: z.string().max(5).optional(),
    timeTo: z.string().max(5).optional(),
    reason: z.string().max(300).optional(),
    address: z.string().max(300).optional(),
    phone: z.string().max(60).optional(),
    shelter: z.string().max(300).optional(),
    /** Кому не виплачувати — вибір на сторінці, ще не збережений у налаштуваннях. */
    penalized: z.array(zId).max(500).optional(),
    /** Яку сторінку документа показати; номер поза межами притискається до краю. */
    page: z.number().int().min(1).max(200).optional(),
    draft: orgSettingsSchema.optional(),
});
/**
 * Рапорт на звільнення: людина й дата. Час, обставини, адреса, телефон і
 * укриття приходять з вкладки; порожні лишають у бланку лінії. Якщо поле
 * не передали — адреса, телефон і укриття беруться з картки людини.
 */
/* --------------------------------------------- сторінка курсанта «Мої рапорти» */
/**
 * Вхід курсанта: один ідентифікаційний код.
 *
 * Код — слабкий секрет: його бачить кожен, хто тримав у руках будь-який
 * папір людини. Єдине, що стоїть на дорозі в перебору, — обмеження спроб
 * з однієї адреси; сильнішого захисту тут свідомо немає, бо сторінка має
 * відкриватися з телефона в одне поле.
 */
export const meLoginSchema = z.object({
    org: z.string().max(64).optional(),
    code: z.string().min(8).max(20),
});
/** Рапорт на харчування за період — те саме, що майстер бота. */
export const meReportSchema = z.object({
    from: zIsoDate,
    to: zIsoDate,
    /** Віддати PDF замість .docx. */
    pdf: z.boolean().optional(),
    /**
     * Віддати картинку першої сторінки — для перегляду на сторінці.
     *
     * Саме картинка, а не PDF: вбудований перегляд PDF на телефоні не слухає
     * `#view=Fit` і показує аркуш у своєму масштабі, тобто наближеним. Картинку
     * ж видно цілою, а збільшує її сам браузер пальцями.
     */
    png: z.boolean().optional(),
    /**
     * Прийоми їжі, за які людина просить компенсацію: `[дата, номер прийому]`.
     *
     * Порожньо — рапорт складається за таблицею, як у діловода. Заповнено —
     * за відповідями людини: індекс позначок підміняється в пам'яті, і в
     * підрахунок таблиці ці клітинки не йдуть. Так само працює майстер бота.
     */
    cells: z
        .array(z.tuple([zIsoDate, z.number().int().min(0).max(15)]))
        .max(500)
        .optional(),
});
/**
 * Що курсант править у себе сам.
 *
 * Рівно ті поля, які він і так диктує боту й які лишаються в рапортах з разу
 * в раз. Ні звання, ні прізвища, ні шаблону харчування тут немає — це справа
 * діловода, і сторінка курсанта до них не дотягується.
 */
export const meProfileSchema = z.object({
    address: z.string().max(300).optional(),
    phone: z.string().max(60).optional(),
    shelter: z.string().max(300).optional(),
    reasons: z.array(z.string().min(1).max(300)).max(12).optional(),
});
/** Проміжок для інтерактивного календаря рапорту. */
export const mePeriodSchema = z.object({ from: zIsoDate, to: zIsoDate });
/** Місяць для картки позначок курсанта. */
export const meMarksSchema = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/) });
/** Звільнення й добове: ті самі поля, що в діловода, тільки без `personId`. */
export const meLeaveSchema = z.object({
    kind: z.enum(['leaveReport', 'dutyReport']),
    date: zIsoDate,
    from: z.string().max(5).optional(),
    to: z.string().max(5).optional(),
    reason: z.string().max(300).optional(),
    address: z.string().max(300).optional(),
    phone: z.string().max(60).optional(),
    shelter: z.string().max(300).optional(),
    pdf: z.boolean().optional(),
    png: z.boolean().optional(),
});
export const leaveRequestSchema = z.object({
    personId: zId,
    date: zIsoDate,
    from: z.string().max(5).optional(),
    to: z.string().max(5).optional(),
    reason: z.string().max(300).optional(),
    address: z.string().max(300).optional(),
    phone: z.string().max(60).optional(),
    shelter: z.string().max(300).optional(),
});
/**
 * Рапорт на добове: ті самі поля, що й на звільнення. Час і підстава, якщо
 * порожні, беруться з налаштувань курсу; адреса, телефон і укриття — з
 * картки, якщо їх не передали з вкладки.
 */
export const dutyRequestSchema = z.object({
    personId: zId,
    date: zIsoDate,
    from: z.string().max(5).optional(),
    to: z.string().max(5).optional(),
    reason: z.string().max(300).optional(),
    address: z.string().max(300).optional(),
    phone: z.string().max(60).optional(),
    shelter: z.string().max(300).optional(),
});
export const fillRequestSchema = z.object({
    from: zIsoDate,
    to: zIsoDate,
    kind: z.string().min(1).max(16),
    presentKind: z.string().max(16).nullable().default(null),
    overwrite: z.boolean().default(false),
    personIds: z.array(zId).optional(),
});
export const exportQuerySchema = z.object({
    from: zIsoDate,
    to: zIsoDate,
    templateId: zId.optional(),
});
export const dutyParseSchema = z.object({
    text: z.string().min(1).max(20000),
    /** Місяць, до якого належать числа без вказаного місяця. */
    year: z.number().int().min(2000).max(2100),
    month: z.number().int().min(1).max(12),
});
//# sourceMappingURL=schemas.js.map